// Bottom Navigation for mobile devices
// Replaces hamburger menu with tab-based navigation

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Build as BuildIcon,
  Inventory as InventoryIcon,
  Schedule as ScheduleIcon,
  MoreHoriz as MoreIcon,
  QrCode as QrCodeIcon,
  People as PeopleIcon,
  ViewColumn as ViewColumnIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function BottomNav({ overdueCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);
  const { isDark, accentBlue, gradientPrimary } = useThemeColors();

  // Build navigation items based on user role
  const getNavItems = () => {
    const items = [];

    // Dashboard - all roles
    items.push({
      key: 'dashboard',
      label: t('layout.dashboard'),
      icon: <DashboardIcon />,
      path: '/',
      showBadge: true,
    });

    // Work Orders - all roles
    items.push({
      key: 'workorders',
      label: t('layout.workOrders'),
      icon: <BuildIcon />,
      path: '/work-orders',
    });

    // Assets - operators, technicians, IT technicians, office managers, supervisors, administrators (not janitors)
    if (hasRole('operator', 'technician', 'it_technician', 'office_manager', 'supervisor', 'administrator')) {
      items.push({
        key: 'assets',
        label: t('layout.assets'),
        icon: <InventoryIcon />,
        path: '/assets',
      });
    }

    // PM Schedules - supervisors, administrators, office managers
    if (hasRole('supervisor', 'administrator', 'office_manager')) {
      items.push({
        key: 'pm',
        label: 'PM',
        icon: <ScheduleIcon />,
        path: '/pm-schedules',
      });
    }

    return items;
  };

  // Additional items for "More" menu
  const getMoreItems = () => {
    const items = [];

    // QR Codes - supervisors, administrators
    if (hasRole('supervisor', 'administrator')) {
      items.push({
        key: 'qrcodes',
        label: t('layout.qrCodes'),
        icon: <QrCodeIcon />,
        path: '/qr-codes',
      });
    }

    // Admin-only items
    if (hasRole('administrator')) {
      items.push(
        { key: 'users', label: t('layout.users'), icon: <PeopleIcon />, path: '/users' },
        { key: 'columns', label: t('layout.columnSettings'), icon: <ViewColumnIcon />, path: '/column-settings' },
        { key: 'roles', label: t('layout.rolePermissions'), icon: <SecurityIcon />, path: '/role-management' },
        { key: 'email', label: t('layout.emailSetup'), icon: <EmailIcon />, path: '/email-setup' },
      );
    }

    return items;
  };

  const navItems = getNavItems();
  const moreItems = getMoreItems();
  const hasMoreItems = moreItems.length > 0;

  // Determine active tab based on current path
  const getActiveTab = () => {
    // Check if current path matches a "More" item
    const moreMatch = moreItems.findIndex(item =>
      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
    );
    if (moreMatch !== -1) {
      return navItems.length; // Return the "More" tab index
    }

    // Check main nav items
    const mainMatch = navItems.findIndex(item =>
      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
    );
    return mainMatch !== -1 ? mainMatch : 0;
  };

  const handleNavChange = (event, newValue) => {
    if (hasMoreItems && newValue === navItems.length) {
      // "More" tab clicked
      setMoreDrawerOpen(true);
    } else if (navItems[newValue]) {
      navigate(navItems[newValue].path);
    }
  };

  const handleMoreItemClick = (path) => {
    setMoreDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Bottom Navigation */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          // Match the top AppBar color in light mode
          background: isDark
            ? undefined
            : gradientPrimary,
          // Safe area for devices with bottom notch (iPhone X+)
          // Add extra padding beyond the safe area to prevent accidental gestures
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        }}
        elevation={8}
      >
        <BottomNavigation
          value={getActiveTab()}
          onChange={handleNavChange}
          showLabels
          sx={{
            height: 72,
            backgroundColor: 'transparent',
            px: 1.5, // Add horizontal padding to move buttons away from screen edges
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '8px 4px',
              // Light mode: white text, Dark mode: default colors
              color: isDark ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                // Light mode: white, Dark mode: bright blue for better readability
                color: isDark ? '#64b5f6' : '#ffffff',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              marginTop: '4px',
              '&.Mui-selected': {
                fontSize: '0.8rem',
                fontWeight: 600,
              },
            },
          }}
        >
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.key}
              label={item.label}
              icon={
                item.showBadge && overdueCount > 0 ? (
                  <Badge
                    badgeContent={overdueCount}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: 16,
                        minWidth: 16,
                      },
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )
              }
            />
          ))}
          {hasMoreItems && (
            <BottomNavigationAction
              label={t('layout.more')}
              icon={<MoreIcon />}
            />
          )}
        </BottomNavigation>
      </Paper>

      {/* More Drawer */}
      <Drawer
        anchor="bottom"
        open={moreDrawerOpen}
        onClose={() => setMoreDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '50vh',
          },
        }}
      >
        <Box sx={{
          p: 2,
          background: isDark ? undefined : gradientPrimary,
          color: isDark ? accentBlue : 'white',
        }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('layout.more')}
          </Typography>
        </Box>
        <List>
          {moreItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                onClick={() => handleMoreItemClick(item.path)}
                selected={location.pathname.startsWith(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(30, 58, 95, 0.1)',
                    borderLeft: `4px solid ${navyBlue}`,
                  },
                }}
              >
                <ListItemIcon sx={{ color: location.pathname.startsWith(item.path) ? accentBlue : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: location.pathname.startsWith(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Spacer to prevent content from being hidden behind bottom nav */}
      <Box sx={{ height: 'calc(72px + env(safe-area-inset-bottom, 0px) + 8px)' }} />
    </>
  );
}
